/**
 * EditorModals
 * Consolidated modal components for EditorPage
 */

import React from 'react'
import { dia, shapes } from '@joint/core'
import { useNavigate } from 'react-router-dom'
import { NoTypesModal } from '../modals/NoTypesModal'
import { SaveModal } from '../modals/SaveModal'
import { ErrorModal } from '../modals/ErrorModal'
import { LayerMappingModal } from '../../../map-editor-features/objectType'
import { AutoLinkModal } from './AutoLinkModal'
import { ExportModal } from './ExportModal'
import { SaveSuccessModal } from './SaveSuccessModal'
import { ExcelImportModal } from '../../../map-editor-features/excel-io'
import type { ApplyResult, ValidationResult } from '../../../map-editor-features/excel-io'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { normalizeMapScale } from '../../../map-editor-features/editor/lib/normalizeMapScale'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import { useCsvRuntimeStore } from '../../../map-editor-features/csv/model/csvStore'
import { useGuidedMapStore } from '../../../map-editor-features/guided-map'
import {
  mapDataGetCsvFileName,
  mapDataGetCsvGroupedLayers,
  mapDataGetCsvParsed,
  mapDataGetCsvRaw,
  mapDataGetCsvSelectedLayers,
} from '../../../map-editor-shared/store/mapDataCsvHelpers'
import { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import type { Floor } from '@/store/floorStore'

interface ModalState {
  noTypes: boolean
  save: boolean
  error: {
    show: boolean
    message: string
  }
  mapping: boolean
  autoLink: boolean
  export: boolean
  excelImport: { show: boolean; fileName?: string }
}

interface EditorModalsProps {
  modalState: ModalState
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>
  autoNavigate: boolean
  setAutoNavigate: (value: boolean) => void
  saveCountdown: number
  onSaveModalConfirm: () => void
  currentFloor: string | null
  currentLotData: any
  graph: dia.Graph | null
  paper: dia.Paper | null
  template: any
  mutableRelationTypes: Record<string, TemplateRelationType>
  onAutoLinkConfirm: (
    adjustedDistances: Record<string, number>,
    enabledRelations: Record<string, boolean>
  ) => Promise<void> | void
  showSaveSuccessModal: boolean
  setShowSaveSuccessModal: (value: boolean) => void
  savedMapId: string | null
  savedThumbnail: string | undefined
  urlProjectId: string | undefined
  /** Validation result from Excel parse — null while parsing or when no file uploaded. */
  excelImportResult: ValidationResult | null
  /** Sheets parser skipped (not matched to any ObjectType). Surfaced as warning banner. */
  excelSkippedSheets: string[]
  /** Apply handler invoked from the Excel modal's "적용" button. */
  onExcelImportApply: (result: ValidationResult) => ApplyResult | Promise<ApplyResult>
}

export const EditorModals: React.FC<EditorModalsProps> = ({
  modalState,
  setModalState,
  autoNavigate,
  setAutoNavigate,
  saveCountdown,
  onSaveModalConfirm,
  currentFloor,
  currentLotData,
  graph,
  paper,
  template,
  mutableRelationTypes,
  onAutoLinkConfirm,
  showSaveSuccessModal,
  setShowSaveSuccessModal,
  savedMapId,
  savedThumbnail,
  urlProjectId,
  excelImportResult,
  excelSkippedSheets,
  onExcelImportApply,
}) => {
  const navigate = useNavigate()
  const floors = useFloorStore((state) => state.floors)

  return (
    <>
      {/* No Types Warning Modal */}
      <NoTypesModal
        isOpen={modalState.noTypes}
        onClose={() => setModalState((prev) => ({ ...prev, noTypes: false }))}
      />

      {/* Save Modal */}
      <SaveModal
        isOpen={modalState.save}
        onClose={() => {
          setModalState((prev) => ({ ...prev, save: false }))
          setAutoNavigate(false)
        }}
        autoNavigate={autoNavigate}
        saveCountdown={saveCountdown}
        onConfirm={onSaveModalConfirm}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={modalState.error.show}
        onClose={() => setModalState((prev) => ({ ...prev, error: { show: false, message: '' } }))}
        message={modalState.error.message}
      />

      {/* Layer Mapping Modal */}
      {modalState.mapping && currentFloor && (
        <LayerMappingModal
          isOpen={modalState.mapping}
          onClose={() => {
            // Cancel layer mapping - restore to previous state or clear
            const currentFloorData = floors.find((f: Floor) => f.id === currentFloor)

            const md = currentFloorData?.mapData
            const csvRaw = mapDataGetCsvRaw(md)
            if (csvRaw && currentFloor) {
              useCsvRuntimeStore.getState().mergeCsvRuntimeForFloor(currentFloor, {
                uploadState: {
                  status: 'parsed',
                  fileName: mapDataGetCsvFileName(md) || 'Restored Data',
                  rowCount: (mapDataGetCsvParsed(md) as { entities?: unknown[] } | null)?.entities?.length || 0,
                },
                rawData: csvRaw,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                parsedData: (mapDataGetCsvParsed(md) as any) ?? null,
                groupedLayers: mapDataGetCsvGroupedLayers(md),
                selectedLayers: new Set(mapDataGetCsvSelectedLayers(md)),
                file: null,
              })
            } else if (currentFloor) {
              useCsvRuntimeStore.getState().mergeCsvRuntimeForFloor(currentFloor, {
                uploadState: { status: 'idle' },
                file: null,
                rawData: null,
                parsedData: null,
                groupedLayers: null,
                selectedLayers: new Set<string>(),
              })
            }

            setModalState((prev) => ({ ...prev, mapping: false }))
          }}
          onConfirm={() => {
            setModalState((prev) => ({ ...prev, mapping: false }))
            // 가이드 진행 중이면 레이어 매핑 단계 완료를 확정 통지한다(아니면
            // no-op). elementCount 관찰만으로는 렌더링 신호가 어긋날 때 가이드가
            // 레이어 매핑 단계에 머물러 멈춘 것처럼 보인다.
            useGuidedMapStore.getState().completeStep('layerMap')
          }}
        />
      )}

      {/* Auto Link Modal */}
      {modalState.autoLink && graph && paper && (
        <AutoLinkModal
          isOpen={modalState.autoLink}
          onClose={() => setModalState((prev) => ({ ...prev, autoLink: false }))}
          onConfirm={onAutoLinkConfirm}
          relationTypes={mutableRelationTypes}
          template={template}
          graph={graph}
          paper={paper}
        />
      )}

      {/* Export Modal */}
      {currentLotData && currentFloor && modalState.export && (() => {
        // Prepare all floors data for export
        const lotFloors = useFloorStore.getState().getFloorsByLotId(currentLotData.id)
        const floorsData = lotFloors.map((floor: Floor) => {
          // 현재 층은 live graph 스냅샷, 나머지 층은 저장된 JSON.
          // 어느 쪽이든 clone 에 normalize 를 적용해 export 좌표를 항상
          // 표준 스케일(주차구역 단변 198.32px, 도면 길이 기반)로 통일한다.
          // 저장본이 normalize 도입(2026-05) 전 원본(DXF) 스케일이거나, 현재 층이
          // 어떤 이유로 로드 시 보정을 건너뛴 경우에도 export 결과는 일정해진다.
          // clone 에만 적용하므로 화면의 live graph 는 변하지 않는다.
          // IMPORTANT: Must provide cellNamespace to restore cells correctly
          const sourceJson = floor.id === currentFloor && graph
            ? graph.toJSON()
            : floor.mapData?.graphJson
          const floorGraph = new dia.Graph({}, { cellNamespace: shapes })
          if (sourceJson) {
            try {
              floorGraph.fromJSON(sourceJson)
              // idempotent — 이미 표준 스케일이면 skip.
              const normalizeResult = normalizeMapScale(floorGraph)
              if (!normalizeResult.applied && normalizeResult.reason === 'no-parking-elements') {
                log.warn(LogCategory.GENERAL,
                  `Export: floor "${floor.name}" has no parking elements — scale not normalized, coordinates exported as stored`)
              }
            } catch (error) {
              console.error(`Failed to restore graph for floor ${floor.name}:`, error)
            }
          }

          return {
            name: floor.name,
            order: floor.order,
            graph: floorGraph,
          }
        })

        return (
          <ExportModal
            isOpen={modalState.export}
            onClose={() => setModalState((prev) => ({ ...prev, export: false }))}
            floors={floorsData}
            lotName={currentLotData.name}
            lotCreated={currentLotData.created}
          />
        )
      })()}

      {/* Save Success Modal */}
      <SaveSuccessModal
        isOpen={showSaveSuccessModal}
        onClose={() => setShowSaveSuccessModal(false)}
        onViewerMode={() => {}} // No-op: Viewer mode removed from editor
        onDashboard={() => {
          setShowSaveSuccessModal(false)
          navigate(`/project/${urlProjectId}/map-editor`)
        }}
        mapId={savedMapId || ''}
        thumbnail={savedThumbnail}
      />

      {/* Excel Import Modal */}
      {modalState.excelImport.show && (
        <ExcelImportModal
          isOpen={modalState.excelImport.show}
          onClose={() => setModalState((prev) => ({ ...prev, excelImport: { show: false } }))}
          result={excelImportResult}
          fileName={modalState.excelImport.fileName}
          skippedSheets={excelSkippedSheets}
          onApply={onExcelImportApply}
        />
      )}
    </>
  )
}
